import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ZoomMtgEmbedded from '@zoom/meetingsdk/embedded';
import {
  Box, Stack, Typography, IconButton, Button, Chip, Menu, MenuItem,
  ListItemIcon, Tooltip, CircularProgress, Snackbar, Alert,
} from '@mui/material';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded';
import CallEndRoundedIcon from '@mui/icons-material/CallEndRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import HeadsetMicRoundedIcon from '@mui/icons-material/HeadsetMicRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { getBearerToken } from '../../utils/Apis';

/**
 * MeetingRoom — ZTALK's custom zaudio-style meeting stage on the Zoom Meeting
 * SDK for Web (Component View, @zoom/meetingsdk 3.12.0).
 *
 * Design reality (researched): the Meeting SDK has NO headless/full-custom-UI
 * mode and NO programmatic audio-start (getMediaStream/startAudio are Video-SDK
 * only). So we: render Zoom's component into a HIDDEN root and draw our own UI;
 * connect computer audio once via a user gesture (the "Connect microphone" step,
 * which clicks Zoom's own Join-Audio control — a browser gesture requirement);
 * then drive push-to-talk with client.mute(). Roster is de-duplicated by a
 * stable key (customerKey = our user id) to hide the ~2-min reconnect "ghosts".
 */

const BRAND_GRAD = 'linear-gradient(135deg, rgba(56,189,248,0.9), rgba(37,99,235,0.9))';
const PTT_RELEASE_RADIUS = 48;

function initials(name) {
  if (!name) return '??';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function PersonAvatar({ name, avatar, talking, size = 64 }) {
  return (
    <Box sx={{
      position: 'relative', width: size, height: size, borderRadius: '50%',
      display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 700, fontSize: size * 0.3,
      background: talking ? '#10b981' : BRAND_GRAD,
      boxShadow: talking
        ? '0 0 0 4px rgba(16,185,129,0.35), 0 0 22px 4px rgba(16,185,129,0.55)'
        : '0 2px 10px rgba(0,0,0,0.35)',
      transition: 'box-shadow 120ms ease, background 120ms ease', overflow: 'hidden',
    }}>
      {avatar ? <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials(name)}
    </Box>
  );
}

const keyOf = (p) => (p && (p.customerKey || p.participantUUID || p.userGuid || (p.userId != null ? String(p.userId) : ''))) || '';

export default function MeetingRoom({ creds, isHost, roomName, onLeave }) {
  const clientRef = useRef(null);
  const rootRef = useRef(null);
  const talkTimers = useRef({});
  const startedRef = useRef(false);
  const endTimerRef = useRef(null);
  const leftRef = useRef(false);

  const [status, setStatus] = useState('joining'); // joining|joined|reconnecting|ended|failed
  const [errorMsg, setErrorMsg] = useState('');
  const [participants, setParticipants] = useState([]);
  const [talkingKeys, setTalkingKeys] = useState(() => new Set());
  const [myKey, setMyKey] = useState(creds.user_key || null);
  const [muted, setMuted] = useState(true);
  const [audioReady, setAudioReady] = useState(false);
  const [connectingAudio, setConnectingAudio] = useState(false);
  const [showAudioPanel, setShowAudioPanel] = useState(false);
  const [menuFor, setMenuFor] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [toast, setToast] = useState(null);

  const notify = (msg, severity = 'info') => setToast({ msg, severity });

  // ---- roster (deduped) ---------------------------------------------------
  const refreshRoster = useCallback(() => {
    const client = clientRef.current;
    if (!client) return;
    let list = [];
    try { list = client.getAttendeeslist() || []; } catch (e) { return; }
    const byKey = new Map();
    for (const p of list) {
      if (!p || p.userId === 0) continue; // skip the id-0 ghost
      const k = keyOf(p) || String(p.userId);
      const prev = byKey.get(k);
      if (!prev || (p.userId || 0) >= (prev.userId || 0)) byKey.set(k, p);
    }
    setParticipants(Array.from(byKey.values()));
    try {
      const me = client.getCurrentUser();
      if (me) { setMyKey(keyOf(me) || String(me.userId)); setMuted(!!me.muted); setAudioReady(me.audio === 'computer'); }
    } catch (e) {}
  }, []);

  const markTalking = useCallback((zoomIds) => {
    const client = clientRef.current;
    let list = [];
    try { list = (client && client.getAttendeeslist()) || []; } catch (e) {}
    const idToKey = new Map(list.map((p) => [p.userId, keyOf(p) || String(p.userId)]));
    const keys = zoomIds.map((id) => idToKey.get(id)).filter(Boolean);
    setTalkingKeys((prev) => { const n = new Set(prev); keys.forEach((k) => n.add(k)); return n; });
    keys.forEach((k) => {
      clearTimeout(talkTimers.current[k]);
      talkTimers.current[k] = setTimeout(() => {
        setTalkingKeys((prev) => { const n = new Set(prev); n.delete(k); return n; });
      }, 900);
    });
  }, []);

  // ---- init + join (once) -------------------------------------------------
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const client = ZoomMtgEmbedded.createClient();
    clientRef.current = client;

    const onUserChange = () => refreshRoster();
    const onActiveSpeaker = (payload) => { if (Array.isArray(payload)) markTalking(payload.map((p) => p.userId)); };
    const onConnectionChange = (payload) => {
      const state = payload && payload.state;
      const reason = payload && payload.reason;
      if (state === 'Connected') {
        clearTimeout(endTimerRef.current);
        setStatus('joined');
      } else if (state === 'Reconnecting') {
        clearTimeout(endTimerRef.current);
        setStatus('reconnecting');
      } else if (state === 'Closed') {
        if (leftRef.current) return;
        if (/host/i.test(String(reason || ''))) { setStatus('ended'); }
        else {
          // could be a failover blip — only declare ended if not restored soon
          clearTimeout(endTimerRef.current);
          endTimerRef.current = setTimeout(() => setStatus('ended'), 6000);
        }
      } else if (state === 'Fail') {
        setStatus('failed'); setErrorMsg('Connection failed.');
      }
    };

    (async () => {
      try {
        await client.init({ zoomAppRoot: rootRef.current, language: 'en-US', patchJsMedia: true });
        client.on('user-added', onUserChange);
        client.on('user-removed', onUserChange);
        client.on('user-updated', onUserChange);
        client.on('active-speaker', onActiveSpeaker);
        client.on('connection-change', onConnectionChange);

        await client.join({
          sdkKey: creds.sdk_key,
          signature: creds.signature,
          meetingNumber: String(creds.meeting_number),
          password: creds.password || '',
          userName: creds.user_name || (isHost ? 'Host' : 'Guest'),
          customerKey: creds.user_key || undefined,
          zak: isHost ? creds.zak : undefined,
          // On Behalf Of token: needed to join a meeting on a customer's own Zoom account.
          obfToken: !isHost && creds.obf_token ? creds.obf_token : undefined,
        });

        setStatus('joined');
        refreshRoster();
        setTimeout(refreshRoster, 1500);
      } catch (e) {
        console.error('Zoom join failed', e);
        setErrorMsg((e && (e.reason || e.message)) || 'Could not join the meeting.');
        setStatus('failed');
      }
    })();

    const graceful = () => { try { client.leaveMeeting(); } catch (e) {} };
    window.addEventListener('pagehide', graceful);
    window.addEventListener('beforeunload', graceful);

    return () => {
      window.removeEventListener('pagehide', graceful);
      window.removeEventListener('beforeunload', graceful);
      Object.values(talkTimers.current).forEach(clearTimeout);
      clearTimeout(endTimerRef.current);
      const c = clientRef.current;
      if (c) {
        ['user-added', 'user-removed', 'user-updated', 'active-speaker', 'connection-change'].forEach((ev) => {
          try { c.off(ev, ev === 'active-speaker' ? onActiveSpeaker : ev === 'connection-change' ? onConnectionChange : onUserChange); } catch (e) {}
        });
        try { c.leaveMeeting(); } catch (e) {}
        try { ZoomMtgEmbedded.destroyClient(); } catch (e) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- audio connect (one-time, needs a user gesture) ---------------------
  const clickNativeJoinAudio = useCallback(() => {
    const root = rootRef.current;
    if (!root) return false;
    const sels = [
      'button[aria-label="Join Audio by Computer"]',
      'button[aria-label*="Computer Audio" i]',
      'button[aria-label*="Join Audio" i]',
      '.join-audio-by-voip__join-btn',
      '[class*="join-audio"] button',
      '[class*="audio-option"] button',
    ];
    for (const s of sels) { const b = root.querySelector(s); if (b) { b.click(); return true; } }
    for (const b of root.querySelectorAll('button')) {
      if (/join audio|computer audio/i.test(b.textContent || '')) { b.click(); return true; }
    }
    return false;
  }, []);

  const connectAudio = useCallback(async () => {
    const client = clientRef.current;
    if (!client || audioReady) return true;
    setConnectingAudio(true);
    clickNativeJoinAudio();
    for (let i = 0; i < 12; i++) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 400));
      let me = null; try { me = client.getCurrentUser(); } catch (e) {}
      if (me && me.audio === 'computer') {
        setAudioReady(true); setConnectingAudio(false); setShowAudioPanel(false);
        try { await client.mute(true); } catch (e) {}
        setMuted(true);
        return true;
      }
      if (i === 2 || i === 5) clickNativeJoinAudio();
    }
    // Could not auto-connect — reveal Zoom's native audio control once.
    setConnectingAudio(false);
    setShowAudioPanel(true);
    return false;
  }, [audioReady, clickNativeJoinAudio]);

  // Poll to auto-close the native audio fallback panel once audio connects.
  useEffect(() => {
    if (!showAudioPanel) return;
    const t = setInterval(() => {
      const client = clientRef.current;
      let me = null; try { me = client && client.getCurrentUser(); } catch (e) {}
      if (me && me.audio === 'computer') { setAudioReady(true); setShowAudioPanel(false); setMuted(!!me.muted); }
    }, 600);
    return () => clearInterval(t);
  }, [showAudioPanel]);

  // ---- push to talk -------------------------------------------------------
  const pttHeld = useRef(false);
  const pttStart = useRef(null);

  const setMic = useCallback(async (on) => {
    const client = clientRef.current;
    if (!client) return;
    try { await client.mute(!on); setMuted(!on); }
    catch (e) { notify('Could not toggle mic.', 'warning'); }
  }, []);

  const pttDown = (e) => {
    if (!audioReady) { connectAudio(); return; }
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (x) {}
    pttHeld.current = true;
    pttStart.current = { x: e.clientX, y: e.clientY };
    setMic(true);
  };
  const pttRelease = () => { if (!pttHeld.current) return; pttHeld.current = false; pttStart.current = null; setMic(false); };
  const pttMove = (e) => {
    if (!pttHeld.current || !pttStart.current) return;
    const dx = e.clientX - pttStart.current.x; const dy = e.clientY - pttStart.current.y;
    if (Math.hypot(dx, dy) <= PTT_RELEASE_RADIUS) return;
    const r = e.currentTarget.getBoundingClientRect();
    const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    if (!inside) pttRelease();
  };

  // ---- host controls ------------------------------------------------------
  const hostMuteOne = async (p) => { try { await clientRef.current.mute(true, p.userId); notify(`Muted ${p.userName || 'participant'}`, 'success'); } catch (e) { notify('Could not mute (host only).', 'error'); } closeMenu(); };
  const hostMuteAll = async () => { try { await clientRef.current.muteAll(true); notify('Muted everyone', 'success'); } catch (e) { notify('Could not mute all.', 'error'); } };
  const hostExpel = async (p) => { try { await clientRef.current.expel(p.userId); notify(`Removed ${p.userName || 'participant'}`, 'success'); } catch (e) { notify('Could not remove.', 'error'); } closeMenu(); };

  const openMenu = (e, p) => { if (!isHost || keyOf(p) === myKey) return; setMenuFor(p); setMenuAnchor(e.currentTarget); };
  const closeMenu = () => { setMenuFor(null); setMenuAnchor(null); };

  const doLeave = async () => {
    leftRef.current = true;
    try { await clientRef.current.leaveMeeting(); } catch (e) {}
    onLeave && onLeave();
  };
  const doEnd = async () => {
    leftRef.current = true;
    try { await clientRef.current.endMeeting(); } catch (e) {}
    try { await getBearerToken('web_host_left?room_id=' + creds.room_id); } catch (e) {}
    onLeave && onLeave();
  };

  // ---- derived ------------------------------------------------------------
  const { host, others } = useMemo(() => {
    let h = null; const rest = [];
    participants.forEach((p) => { if (p.isHost && !h) h = p; else rest.push(p); });
    return { host: h, others: rest };
  }, [participants]);

  const isJoined = status === 'joined' || status === 'reconnecting';

  // ---- render -------------------------------------------------------------
  return (
    <Box sx={{ position: 'fixed', inset: 0, bgcolor: '#0b1020', color: '#e2e8f0', display: 'flex', flexDirection: 'column', zIndex: 1300 }}>
      {/* hidden Zoom component root; only surfaced inside the audio fallback modal */}
      <Box ref={rootRef} sx={showAudioPanel
        ? { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 'min(92vw,460px)', height: 'min(70vh,420px)', zIndex: 3001, overflow: 'auto', borderRadius: 2, bgcolor: '#000' }
        : { position: 'fixed', left: -99999, top: 0, width: 900, height: 600, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }} />

      {showAudioPanel && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 3000, bgcolor: 'rgba(0,0,0,0.75)', display: 'grid', placeItems: 'center', p: 2 }}>
          <Box sx={{ position: 'absolute', top: 24, left: 0, right: 0, textAlign: 'center', px: 3 }}>
            <Typography sx={{ color: '#fff', fontWeight: 600 }}>Tap “Join Audio” once to turn on your microphone</Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>This closes automatically once your mic is connected.</Typography>
          </Box>
          <IconButton onClick={() => setShowAudioPanel(false)} sx={{ position: 'fixed', top: 12, right: 12, zIndex: 3002, bgcolor: 'rgba(255,255,255,0.12)', color: '#fff' }}><CloseRoundedIcon /></IconButton>
        </Box>
      )}

      {/* top bar */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 34, height: 34, borderRadius: 2, background: BRAND_GRAD, display: 'grid', placeItems: 'center', fontWeight: 800, color: '#fff' }}>Z</Box>
          <Box>
            <Typography variant="subtitle1" sx={{ color: '#fff', lineHeight: 1.1 }}>{roomName || 'Meeting'}</Typography>
            <Typography variant="caption" sx={{ color: status === 'reconnecting' ? '#fbbf24' : '#94a3b8' }}>
              {status === 'joined' ? `${participants.length} in room`
                : status === 'reconnecting' ? 'Reconnecting…'
                : status === 'joining' ? 'Connecting…'
                : status === 'ended' ? 'Meeting ended' : status}
            </Typography>
          </Box>
        </Stack>
        {isHost && isJoined && (
          <Button size="small" onClick={hostMuteAll} startIcon={<VolumeOffRoundedIcon />} variant="outlined" sx={{ color: '#cbd5e1' }}>Mute all</Button>
        )}
      </Stack>

      {/* stage */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
        {status === 'joining' && (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }} spacing={2}>
            <CircularProgress sx={{ color: '#38bdf8' }} /><Typography sx={{ color: '#94a3b8' }}>Connecting to the meeting…</Typography>
          </Stack>
        )}
        {status === 'failed' && (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }} spacing={2}>
            <Typography variant="h6" sx={{ color: '#fff' }}>Couldn’t join</Typography>
            <Typography sx={{ color: '#f87171', maxWidth: 420, textAlign: 'center' }}>{errorMsg}</Typography>
            <Button variant="contained" onClick={onLeave}>Go back</Button>
          </Stack>
        )}
        {status === 'ended' && (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }} spacing={2}>
            <Typography variant="h6" sx={{ color: '#fff' }}>Meeting ended</Typography>
            <Button variant="contained" onClick={onLeave}>Back to rooms</Button>
          </Stack>
        )}

        {isJoined && (
          <Stack spacing={4} alignItems="center">
            {host && (
              <Stack alignItems="center" spacing={1}>
                <Box sx={{ position: 'relative', cursor: isHost && keyOf(host) !== myKey ? 'pointer' : 'default' }} onClick={(e) => openMenu(e, host)}>
                  <PersonAvatar name={host.userName} avatar={host.avatar} talking={talkingKeys.has(keyOf(host))} size={96} />
                  <Chip size="small" icon={<WorkspacePremiumRoundedIcon sx={{ color: '#fbbf24 !important' }} />} label="Host"
                    sx={{ position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)', bgcolor: '#1e293b', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.4)' }} />
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography sx={{ color: '#fff', fontWeight: 600 }}>{host.userName}{keyOf(host) === myKey && ' (you)'}</Typography>
                  {host.muted ? <MicOffRoundedIcon sx={{ fontSize: 16, color: '#64748b' }} /> : <MicRoundedIcon sx={{ fontSize: 16, color: '#34d399' }} />}
                </Stack>
              </Stack>
            )}

            <Box sx={{ width: '100%', maxWidth: 760, display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(5, 1fr)' }, gap: 2.5 }}>
              {others.map((p) => (
                <Stack key={keyOf(p)} alignItems="center" spacing={0.75} sx={{ cursor: isHost && keyOf(p) !== myKey ? 'pointer' : 'default' }} onClick={(e) => openMenu(e, p)}>
                  <PersonAvatar name={p.userName} avatar={p.avatar} talking={talkingKeys.has(keyOf(p))} size={64} />
                  <Stack direction="row" spacing={0.4} alignItems="center" sx={{ maxWidth: '100%' }}>
                    <Typography noWrap variant="caption" sx={{ color: '#cbd5e1', fontWeight: 500 }}>{p.userName}{keyOf(p) === myKey && ' (you)'}</Typography>
                    {p.muted && <MicOffRoundedIcon sx={{ fontSize: 13, color: '#64748b' }} />}
                  </Stack>
                </Stack>
              ))}
            </Box>

            {others.length === 0 && !host && <Typography sx={{ color: '#64748b' }}>Waiting for people to join…</Typography>}
          </Stack>
        )}
      </Box>

      {/* bottom control bar */}
      {isJoined && (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2.5}
          sx={{ px: 2, py: 2.5, borderTop: '1px solid rgba(255,255,255,0.08)', bgcolor: 'rgba(0,0,0,0.25)' }}>
          {!audioReady ? (
            <Button onClick={connectAudio} disabled={connectingAudio} startIcon={connectingAudio ? <CircularProgress size={18} color="inherit" /> : <HeadsetMicRoundedIcon />}
              sx={{ px: 4, py: 1.75, borderRadius: 999, fontWeight: 700, color: '#fff', background: BRAND_GRAD, '&:hover': { background: BRAND_GRAD } }}>
              {connectingAudio ? 'Connecting mic…' : 'Connect microphone'}
            </Button>
          ) : (
            <Button onPointerDown={pttDown} onPointerUp={pttRelease} onPointerCancel={pttRelease} onPointerMove={pttMove}
              onContextMenu={(e) => e.preventDefault()} disableRipple
              sx={{
                userSelect: 'none', touchAction: 'none', px: 4, py: 1.75, borderRadius: 999, fontWeight: 700, fontSize: '1rem', color: '#fff',
                background: !muted ? '#10b981' : BRAND_GRAD,
                boxShadow: !muted ? '0 0 26px 4px rgba(16,185,129,0.55)' : '0 6px 18px rgba(79,70,229,0.5)',
                '&:hover': { background: !muted ? '#10b981' : BRAND_GRAD },
              }}
              startIcon={!muted ? <MicRoundedIcon /> : <MicOffRoundedIcon />}>
              {!muted ? 'Talking… release to mute' : 'Hold to talk'}
            </Button>
          )}

          <Tooltip title="Leave (room stays open)">
            <IconButton onClick={doLeave} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', width: 52, height: 52, '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' } }}>
              <LogoutRoundedIcon />
            </IconButton>
          </Tooltip>
          {isHost && (
            <Tooltip title="End meeting for everyone">
              <IconButton onClick={doEnd} sx={{ bgcolor: '#dc2626', color: '#fff', width: 52, height: 52, '&:hover': { bgcolor: '#b91c1c' } }}>
                <CallEndRoundedIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}

      <Menu anchorEl={menuAnchor} open={!!menuFor} onClose={closeMenu}>
        {menuFor && [
          <MenuItem key="mute" onClick={() => hostMuteOne(menuFor)}><ListItemIcon><MicOffRoundedIcon fontSize="small" /></ListItemIcon>Mute</MenuItem>,
          <MenuItem key="kick" onClick={() => hostExpel(menuFor)} sx={{ color: 'error.main' }}><ListItemIcon><PersonRemoveRoundedIcon fontSize="small" color="error" /></ListItemIcon>Remove</MenuItem>,
        ]}
      </Menu>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
